import React, { useState, useEffect, useCallback, forwardRef, InputHTMLAttributes, useId } from 'react';
import { Search, X } from 'lucide-react';
import { Spinner } from './Spinner';

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  isLoading?: boolean;
  className?: string;
  label?: string;
  fullWidth?: boolean;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Search...',
      debounceMs = 300,
      isLoading = false,
      className = '',
      label,
      fullWidth = false,
      disabled,
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;

    // Internal state for immediate UI updates
    const [internalValue, setInternalValue] = useState(value);

    // Sync internal value when external value changes
    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    // Debounced onChange callback
    useEffect(() => {
      const timer = setTimeout(() => {
        if (internalValue !== value) {
          onChange(internalValue);
        }
      }, debounceMs);

      return () => clearTimeout(timer);
    }, [internalValue, debounceMs, onChange, value]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
    }, []);

    const handleClear = useCallback(() => {
      setInternalValue('');
      onChange('');
    }, [onChange]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape' && internalValue) {
        handleClear();
      }
    }, [internalValue, handleClear]);

    return (
      <div className={`${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {/* Search icon on the left */}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>

          <input
            ref={ref}
            id={id}
            type="text"
            value={internalValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={`
              block w-full rounded-lg border bg-white
              pl-10 pr-10 py-2 text-sm
              placeholder:text-gray-400
              focus:outline-none focus:ring-2 focus:ring-offset-0
              focus:border-blue-500 focus:ring-blue-500
              disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
              border-gray-300 text-gray-900
              ${className}
            `.trim().replace(/\s+/g, ' ')}
            aria-label={label || placeholder}
            {...props}
          />

          {/* Right side: loading spinner or clear button */}
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {isLoading ? (
              <Spinner size="sm" className="text-gray-400" />
            ) : internalValue ? (
              <button
                type="button"
                onClick={handleClear}
                disabled={disabled}
                className="
                  p-1 rounded-md text-gray-400
                  hover:text-gray-600 hover:bg-gray-100
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
                aria-label="Clear search"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
